/**
 * recent-changes.ts — queue #658 §UAT-2026-07-08-R658 §R658-2.
 *
 * 전역 변경 피드용 순수 유틸 — §R643 이 이미 커밋한 `module-maturity.json`
 * (`modules[artifact] = { sourceDir, lastChanged, commitCount, commits[{date, subject}], gitAvailable }`)
 * 을 read-only 재파생한다. 신규 데이터 원천/추출 파이프라인 없음(R640 `facet-predicates.ts`/
 * §R643 `maturity.ts` 순수-파생+단위테스트 패턴 답습).
 *
 * ★일자 시한폭탄 금지 — `isRecentlyChanged(lastChanged, now)` 의 `now` 는 필수 주입 파라미터.
 * 호출부는 `new Date()`, 테스트는 고정 Date 를 넘긴다. 실데이터/`Date.now()` 의존 단언 금지.
 */
import type { ModuleMaturity, ModuleMaturityCommit, ModuleNode } from '../types/showcase';

/** 병합된 전역 변경 항목(모듈별 commits[] 를 flatten) */
export interface ChangeEntry {
  date: string;
  artifact: string;
  subject: string;
}

/** 단일 모듈 변경 이력(queue #672 §R672-3) — `ChangeHistorySection` 렌더용. */
export interface ModuleHistory {
  artifact: string;
  lastChanged: string | null;
  commitCount: number;
  /** isRecentlyChanged(lastChanged, now) 재사용(§R658) */
  recentlyChanged: boolean;
  /** 이미 일자 내림차순(추출 단계 정렬) · hash? 포함 */
  commits: ModuleMaturityCommit[];
}

/** _index 대시보드 카드용 — 모듈 단위(dedupe) 최근 변경 정보 */
export interface RecentModule {
  artifact: string;
  lastChanged: string | null;
  sourceDir: string;
}

/**
 * 전 artifact 의 commits[] 를 {date, artifact, subject} 로 flatten 후 일자 내림차순 정렬.
 * 동일자는 artifact 알파벳 안정 tie-break(Array.prototype.sort 는 stable 이므로 1차 정렬
 * 후 2차 비교 키만 추가하면 됨). gitAvailable:false/빈 commits 는 자연 제외.
 */
export function mergeModuleChanges(maturity: ModuleMaturity): ChangeEntry[] {
  const entries: ChangeEntry[] = [];
  for (const [artifact, entry] of Object.entries(maturity.modules ?? {})) {
    if (!entry?.commits?.length) continue;
    for (const commit of entry.commits) {
      entries.push({ date: commit.date, artifact, subject: commit.subject });
    }
  }
  return entries.sort((a, b) => {
    const byDate = b.date.localeCompare(a.date);
    if (byDate !== 0) return byDate;
    return a.artifact.localeCompare(b.artifact);
  });
}

/** 상위 n건(이미 정렬된 목록 기준 slice). */
export function topRecentChanges(entries: ChangeEntry[], n: number): ChangeEntry[] {
  return entries.slice(0, n);
}

/**
 * `lastChanged` 기준 내림차순(null 은 뒤) 상위 n 모듈 — _index 카드용(모듈 단위 dedupe).
 */
export function selectRecentModules(maturity: ModuleMaturity, n: number): RecentModule[] {
  const modules: RecentModule[] = Object.entries(maturity.modules ?? {}).map(([artifact, entry]) => ({
    artifact,
    lastChanged: entry?.lastChanged ?? null,
    sourceDir: entry?.sourceDir ?? '',
  }));
  return modules
    .sort((a, b) => {
      if (a.lastChanged == null && b.lastChanged == null) return a.artifact.localeCompare(b.artifact);
      if (a.lastChanged == null) return 1;
      if (b.lastChanged == null) return -1;
      const byDate = b.lastChanged.localeCompare(a.lastChanged);
      if (byDate !== 0) return byDate;
      return a.artifact.localeCompare(b.artifact);
    })
    .slice(0, n);
}

/**
 * `lastChanged` 가 `now` 기준 `days`일 이내인지 판정. `lastChanged==null` → false.
 * ★`now` 는 필수 주입 파라미터 — 호출부 `new Date()`, 테스트는 고정 Date.
 */
export function isRecentlyChanged(lastChanged: string | null, now: Date, days = 14): boolean {
  if (lastChanged == null) return false;
  const parsed = Date.parse(lastChanged);
  if (Number.isNaN(parsed)) return false;
  const diffMs = now.getTime() - parsed;
  return diffMs >= 0 && diffMs <= days * 86_400_000;
}

/**
 * 단일 모듈(artifact)의 변경 이력 select(queue #672 §R672-3 — 전역 병합 `mergeModuleChanges`
 * 와 대칭). 신규 데이터 원천/추출 파이프라인 0 · `isRecentlyChanged` 재사용(§R658 `now` 주입
 * 관례 답습 · Date.now() 의존 금지). 대상 artifact 부재·`gitAvailable:false`·`commits` 0건은
 * `null`(호출부가 섹션 미렌더 — §R665/§R666 0건 숨김 계보).
 */
export function selectModuleHistory(maturity: ModuleMaturity, artifact: string, now: Date): ModuleHistory | null {
  const entry = maturity.modules?.[artifact];
  if (!entry || !entry.gitAvailable || !entry.commits?.length) return null;
  return {
    artifact,
    lastChanged: entry.lastChanged,
    commitCount: entry.commitCount,
    recentlyChanged: isRecentlyChanged(entry.lastChanged, now),
    commits: entry.commits,
  };
}

/** _index '내 구독 모듈 최근 변경' 위젯용 — 구독 코드 1건의 변경 1건(queue #677) */
export interface WatchedChangeEntry {
  code: string;
  artifact: string;
  date: string;
  subject: string;
  hash?: string;
}

/**
 * 구독 코드(`watchedCodes`) × 카탈로그 노드(code→artifact) × `module-maturity.json` commits
 * 를 조합해 최신순 상위 n(queue #677 §UAT-2026-07-13-R677 §R677-4③). 신규 집계 endpoint
 * 0(§R672 `selectModuleHistory` 와 동일 커밋 메타 재사용) — 매핑 실패 코드(노드 없음 ·
 * artifact 미가용 · commits 0건)는 자연 스킵(§R665/§R666 0건 숨김 계보). `code` 는 딥링크
 * (`/showcase#<code>` · §R679 재사용)용으로 각 항목에 보존한다.
 */
export function selectWatchedRecentChanges(
  maturity: ModuleMaturity,
  nodes: ModuleNode[],
  watchedCodes: string[],
  n: number
): WatchedChangeEntry[] {
  const codeToArtifact = new Map(nodes.map((node) => [node.code, node.artifact]));
  const entries: WatchedChangeEntry[] = [];

  for (const code of watchedCodes) {
    const artifact = codeToArtifact.get(code);
    if (!artifact) continue;
    const entry = maturity.modules?.[artifact];
    if (!entry?.gitAvailable || !entry.commits?.length) continue;
    for (const commit of entry.commits) {
      entries.push({ code, artifact, date: commit.date, subject: commit.subject, hash: commit.hash });
    }
  }

  return entries
    .sort((a, b) => {
      const byDate = b.date.localeCompare(a.date);
      if (byDate !== 0) return byDate;
      return a.code.localeCompare(b.code);
    })
    .slice(0, n);
}

/** artifact → 첫 코드(오름차순) 매핑 — 딥링크 해석용(`/showcase#<code>`). */
export function artifactToFirstCode(nodes: ModuleNode[]): Map<string, string> {
  const map = new Map<string, string>();
  const sorted = [...nodes].sort((a, b) => a.code.localeCompare(b.code));
  for (const node of sorted) {
    if (!map.has(node.artifact)) {
      map.set(node.artifact, node.code);
    }
  }
  return map;
}
